import React from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  Circle,
  ArrowRight,
  Sparkles,
  Building2,
  Share2,
  Wand2,
  CalendarPlus,
  X as XIcon,
  Lock,
} from "lucide-react";

/**
 * OnboardingChecklist
 * -------------------
 * First-run "Get up and running" widget for the Dashboard. Rendered ONLY
 * while the user has meaningful setup steps left. The parent decides
 * whether to render this at all — this component just renders whatever
 * items it's given and reports dismissal back up.
 *
 * Item shape (per row):
 *   {
 *     key: "profile" | "generate" | "connect" | "schedule",
 *     label: string,                  // primary line
 *     hint:  string,                  // secondary explanation
 *     done:  boolean,                 // renders as line-through when true
 *     to:    string | null,           // where the CTA points
 *     cta:   string,                  // CTA label
 *     blocked: boolean,               // Meta-review-pending state — NOT a failure
 *     blocked_note: string,           // shown when blocked=true instead of the CTA
 *     Icon:  LucideIcon,              // optional per-row icon
 *   }
 *
 * ``blocked`` is the critical addition (spec R1): when Meta App Review is
 * pending, we WANT the "Connect a social account" step to show up honestly
 * — but NOT to look like the user forgot to do it. Blocked rows render
 * with a "Pending Meta review" pill instead of a nag CTA and do not count
 * against the "all steps complete" auto-hide check.
 */
export default function OnboardingChecklist({ items, onDismiss }) {
  if (!items || !items.length) return null;
  // Only the "actionable" (non-blocked) items count toward completion. This
  // matches the spec — a Meta-blocked step must not force the checklist to
  // stay up forever while the review is pending.
  const actionable = items.filter((i) => !i.blocked);
  const done = actionable.filter((i) => i.done).length;
  const total = actionable.length || items.length;
  const pct = Math.round((done / Math.max(1, total)) * 100);

  return (
    <div className="rounded-[16px] glass p-5 relative" data-testid="onboarding-checklist">
      {typeof onDismiss === "function" ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss onboarding checklist"
          title="Dismiss"
          data-testid="onboarding-checklist-dismiss"
          className="absolute top-3 right-3 h-7 w-7 rounded-md flex items-center justify-center text-[color:var(--text-3)] hover:text-[color:var(--text-1)] hover:bg-[color:var(--surface-2)] transition"
        >
          <XIcon className="h-4 w-4" />
        </button>
      ) : null}
      <div className="flex items-center gap-3 mb-3 pr-8">
        <div className="h-9 w-9 rounded-md bg-[color:rgba(27,168,159,0.12)] text-[color:var(--brand-primary)] flex items-center justify-center">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg font-semibold">Get up and running</div>
          <div className="text-xs text-[color:var(--text-3)]">Finish setup so Postmoro can think on your behalf.</div>
        </div>
        <div className="text-xs font-mono-tabular text-[color:var(--text-2)]" data-testid="onboarding-checklist-progress">
          {done}/{total}
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-[color:var(--surface-3)] overflow-hidden mb-4">
        <div
          className="h-full rounded-full bg-gradient-to-r from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] transition-all duration-700"
          style={{ width: pct + "%" }}
        />
      </div>
      <ul className="space-y-2">
        {items.map((it) => {
          const Icon = it.Icon || Circle;
          return (
            <li
              key={it.key}
              data-testid={`onboarding-item-${it.key}`}
              className={
                "flex items-center gap-3 rounded-md border p-3 " +
                (it.blocked
                  ? "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] opacity-90"
                  : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]")
              }
            >
              {it.done ? (
                <CheckCircle2 className="h-5 w-5 text-[color:var(--success)] shrink-0" />
              ) : it.blocked ? (
                <Lock className="h-5 w-5 text-[color:var(--text-3)] shrink-0" />
              ) : (
                <Icon className="h-5 w-5 text-[color:var(--text-3)] shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div
                  className={
                    "text-sm " +
                    (it.done
                      ? "text-[color:var(--text-3)] line-through"
                      : "text-[color:var(--text-1)] font-medium")
                  }
                >
                  {it.label}
                </div>
                {it.hint && !it.done ? (
                  <div className="text-xs text-[color:var(--text-3)] mt-0.5">{it.hint}</div>
                ) : null}
              </div>
              {it.blocked ? (
                <span
                  className="text-[10px] uppercase tracking-wider text-[color:var(--accent-warm)] whitespace-nowrap"
                  data-testid={`onboarding-item-${it.key}-blocked-pill`}
                >
                  {it.blocked_note || "Pending review"}
                </span>
              ) : !it.done && it.to ? (
                <Link
                  to={it.to}
                  className="text-xs inline-flex items-center gap-1 text-[color:var(--brand-primary)] hover:opacity-90 whitespace-nowrap"
                  data-testid={`onboarding-step-${it.key}`}
                >
                  {it.cta || "Go"} <ArrowRight className="h-3 w-3" />
                </Link>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Builder for the concrete checklist items shown on the Dashboard.
 * Ordering matches the spec exactly:
 *   1. Complete business profile
 *   2. Generate first post
 *   3. Connect a social account
 *   4. Schedule / publish first post
 *
 * ``connectionsPayload`` is the shape returned by ``GET /api/connections``
 * ({ platforms: [ { key, connected, mode, awaiting_app_review, ... } ] })
 * so we can treat X and LinkedIn as valid social connections without
 * making Meta blocking. If ONLY Meta platforms are available (both
 * ``awaiting_app_review``) and the user hasn't connected X or LinkedIn,
 * the row is marked ``blocked`` — visible but not a nag.
 *
 * ``posts`` is the raw ``/api/posts`` list from the Dashboard load.
 */
export function defaultOnboardingItems({ profile, posts, connectionsPayload }) {
  const postsArr = Array.isArray(posts) ? posts : [];

  // Step 1 — profile complete when the wizard's minimum required fields are set.
  const profileDone = !!(profile && profile.business_name && profile.business_type);

  // Step 2 — first post exists in any state (draft counts — it means the
  // user has actually clicked Generate and produced something).
  const anyPostExists = postsArr.length > 0;

  // Step 3 — Any real (non-demo) social connection on any launch platform
  // counts. X and LinkedIn count if connected in "real" mode. Meta
  // platforms count too when review is done and the user has a live
  // connection — but until then, if X and LinkedIn also aren't connected,
  // we render the row as ``blocked`` so the checklist doesn't lie about
  // the user's actual completion status.
  const platforms = (connectionsPayload && connectionsPayload.platforms) || [];
  const realConnection = platforms.find(
    (p) => p.connected && p.mode === "real" && !p.awaiting_app_review,
  );
  const nonMetaConnectable = platforms.filter(
    (p) => !p.awaiting_app_review && !p.coming_soon,
  );
  // "All my connect options are Meta-blocked" → still show the row, but
  // as an honest Pending-review state instead of a nag.
  const onlyMetaAvailable =
    nonMetaConnectable.length === 0 && platforms.length > 0;
  const connectDone = !!realConnection;
  const connectBlocked = !connectDone && onlyMetaAvailable;

  // Step 4 — Schedule / publish first post. Any post beyond draft counts:
  // scheduled, published, partial_published, pending_approval.
  const publishedOrScheduled = postsArr.some((p) => {
    const s = String(p?.status || "").toLowerCase();
    return (
      s === "scheduled" ||
      s === "published" ||
      s === "partial_published" ||
      s === "pending_approval"
    );
  });

  return [
    {
      key: "profile",
      label: "Complete business profile",
      hint: "Business name, type, and voice — so the AI can sound like you.",
      done: profileDone,
      to: "/dashboard",
      cta: profileDone ? "Edit" : "Edit profile",
      Icon: Building2,
    },
    {
      key: "generate",
      label: "Generate your first post",
      hint: "Give us a topic and we'll draft the caption, hashtags, and image.",
      done: anyPostExists,
      to: "/generate",
      cta: "Generate",
      Icon: Wand2,
    },
    {
      key: "connect",
      label: "Connect a social account",
      hint: connectBlocked
        ? "Facebook & Instagram are pending Meta App Review — no action needed from you right now."
        : "Publish directly to Facebook, Instagram, X, or LinkedIn.",
      done: connectDone,
      to: connectBlocked ? null : "/settings/connections",
      cta: "Connect",
      blocked: connectBlocked,
      blocked_note: "Pending Meta review",
      Icon: Share2,
    },
    {
      key: "schedule",
      label: "Schedule or publish your first post",
      hint: "Pick a time from Content Manager or hit Publish now from a post.",
      done: publishedOrScheduled,
      to: "/content",
      cta: "Open content",
      Icon: CalendarPlus,
    },
  ];
}

/**
 * Convenience selector matching the spec:
 *   "Automatically stop showing it once the meaningful onboarding/setup
 *    steps are complete."
 *
 * Blocked rows (Meta-review-pending) are NOT counted here — we don't
 * force the checklist to stay up because of something the user can't act
 * on. When the profile is set AND the user has generated something AND
 * scheduled/published at least one post, the checklist is done, even if
 * the "connect social" row is still stuck in Meta review.
 */
export function isChecklistComplete(items) {
  if (!Array.isArray(items) || !items.length) return true;
  return items.every((i) => i.done || i.blocked);
}
